import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { Target, Save, Trash2, Briefcase, AlertTriangle, CheckCircle, X, Bell, Search, Plus } from 'lucide-react';
import { formatMoney, formatMoneyDetail, formatNumber } from '../../utils/formatters';
import { getCategoryColor } from '../../utils/portfolio';
import { sendTelegramAlert, formatConnectSuccess } from '../../services/telegramService';
import { getQuote } from '../../services/marketDataService';
import { getAuth } from 'firebase/auth';
import { logEvent, EVENTS } from '../../services/eventLogger';
import { usePlan } from '../../contexts/PlanContext';
import PaywallModal from '../common/PaywallModal';
import { useAllSymbols } from '../../hooks/useAllSymbols';

// THB-aware formatters for GOLDTHB
const fmtPrice = (sym, val) => sym === 'GOLDTHB'
  ? `฿${new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(val)}`
  : formatMoneyDetail(val);
const fmtValue = (sym, val) => sym === 'GOLDTHB'
  ? `฿${new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(val)}`
  : formatMoney(val);

const TELEGRAM_LINK_URL = 'https://us-central1-investport-pro.cloudfunctions.net/createTelegramLink';

const getLabels = (language) => language === 'en'
  ? {
    title: 'Buy the Dip Planner',
    subtitle: 'Set support levels for any stock. Get alerts when prices fall to your target buy zones.',
    selectAsset: 'Select Asset',
    searchPlaceholder: '🔍 Type any symbol (e.g. AAPL, NVDA)...',
    units: 'units',
    noAssetFound: 'No matching holdings found',
    holdingQty: 'Current quantity',
    avgCost: 'Average cost',
    currentPrice: 'Current price',
    totalValue: 'Total value',
    support1: 'Support Level 1',
    support2: 'Support Level 2',
    support3: 'Support Level 3',
    support4: 'Support Level 4',
    support5: 'Support Level 5',
    support6: 'Support Level 6',
    support7: 'Support Level 7',
    support1Hint: 'First dip target (nearest)',
    support2Hint: 'Second dip target',
    support3Hint: 'Third dip target',
    support4Hint: 'Fourth dip target',
    support5Hint: 'Deep dip zone',
    support6Hint: 'Crash target',
    support7Hint: 'Black swan target (deepest)',
    save: 'Save Targets',
    saved: 'Saved!',
    delete: 'Delete Target',
    emptyTitle: 'Select an asset to set support levels',
    emptySubtitle: 'Use the search box above to pick a holding',
    summaryTitle: 'All Saved Support Levels',
    summarySubtitle: 'Overview of all your buy-the-dip targets',
    noTargets: 'No support levels saved yet',
    noTargetsSubtitle: 'Select a holding above and set your buy-the-dip targets',
    statusActive: 'At Support!',
    statusBelow: 'Below Support!',
    statusAbove: 'Above',
    dropFromCurrent: 'Drop from current price',
    pricePlaceholder: 'e.g. 150.00',
    savedTargets: 'targets saved'
  }
  : {
    title: 'วางแผนช้อนหุ้น',
    subtitle: 'ตั้งแนวรับสำหรับหุ้นที่สนใจ ระบบจะแจ้งเตือนเมื่อราคาตกลงมาถึงเป้าหมาย',
    selectAsset: 'เลือกสินทรัพย์',
    searchPlaceholder: '🔍 พิมพ์ชื่อหุ้น (เช่น AAPL, NVDA)...',
    units: 'หน่วย',
    noAssetFound: 'พิมพ์ชื่อหุ้นแล้วกด Enter เพื่อเพิ่ม',
    holdingQty: 'จำนวนถือครอง',
    avgCost: 'ต้นทุนเฉลี่ย',
    currentPrice: 'ราคาปัจจุบัน',
    totalValue: 'มูลค่ารวม',
    support1: 'แนวรับที่ 1',
    support2: 'แนวรับที่ 2',
    support3: 'แนวรับที่ 3',
    support4: 'แนวรับที่ 4',
    support5: 'แนวรับที่ 5',
    support6: 'แนวรับที่ 6',
    support7: 'แนวรับที่ 7',
    support1Hint: 'เป้าหมายช้อนแรก (ใกล้สุด)',
    support2Hint: 'เป้าหมายช้อนที่ 2',
    support3Hint: 'เป้าหมายช้อนที่ 3',
    support4Hint: 'เป้าหมายช้อนที่ 4',
    support5Hint: 'แนวรับลึก',
    support6Hint: 'แนวรับยาม Crash',
    support7Hint: 'เป้าวิกฤต (Black Swan)',
    save: 'บันทึกเป้าหมาย',
    saved: 'บันทึกแล้ว!',
    delete: 'ลบเป้าหมาย',
    emptyTitle: 'เลือกสินทรัพย์เพื่อตั้งแนวรับ',
    emptySubtitle: 'คลิกที่ช่องค้นหาด้านบน',
    summaryTitle: 'แนวรับที่บันทึกไว้ทั้งหมด',
    summarySubtitle: 'ภาพรวมเป้าหมายช้อนหุ้นทั้งหมดของคุณ',
    noTargets: 'ยังไม่มีแนวรับที่บันทึกไว้',
    noTargetsSubtitle: 'เลือกหุ้นด้านบนแล้วตั้งเป้าหมายแนวรับ',
    statusActive: 'ถึงแนวรับ!',
    statusBelow: 'ต่ำกว่าแนวรับ!',
    statusAbove: 'ยังไม่ถึง',
    dropFromCurrent: 'ลดจากราคาปัจจุบัน',
    pricePlaceholder: 'เช่น 150.00',
    savedTargets: 'เป้าหมายที่บันทึก'
  };

// helper: get status of current price vs a support level
const getSupportStatus = (currentPrice, supportPrice) => {
  if (!supportPrice || supportPrice <= 0 || !currentPrice) return null;
  const diff = currentPrice - supportPrice;
  const pct = (diff / supportPrice) * 100;
  if (pct <= 0) return { type: 'below', pct }; // price is at or below support
  if (pct <= 3) return { type: 'near', pct }; // within 3% of support
  return { type: 'above', pct };
};

const SUPPORT_COLORS = [
  { bg: 'bg-emerald-50', bgDark: 'bg-emerald-900/30', border: 'border-emerald-200', borderDark: 'border-emerald-700', text: 'text-emerald-700', textDark: 'text-emerald-400', dot: 'bg-emerald-500', ring: 'ring-emerald-100', ringDark: 'ring-emerald-900/50', gradient: 'from-emerald-500 to-teal-500' },
  { bg: 'bg-teal-50', bgDark: 'bg-teal-900/30', border: 'border-teal-200', borderDark: 'border-teal-700', text: 'text-teal-700', textDark: 'text-teal-400', dot: 'bg-teal-500', ring: 'ring-teal-100', ringDark: 'ring-teal-900/50', gradient: 'from-teal-500 to-cyan-500' },
  { bg: 'bg-amber-50', bgDark: 'bg-amber-900/30', border: 'border-amber-200', borderDark: 'border-amber-700', text: 'text-amber-700', textDark: 'text-amber-400', dot: 'bg-amber-500', ring: 'ring-amber-100', ringDark: 'ring-amber-900/50', gradient: 'from-amber-500 to-orange-500' },
  { bg: 'bg-orange-50', bgDark: 'bg-orange-900/30', border: 'border-orange-200', borderDark: 'border-orange-700', text: 'text-orange-700', textDark: 'text-orange-400', dot: 'bg-orange-500', ring: 'ring-orange-100', ringDark: 'ring-orange-900/50', gradient: 'from-orange-500 to-red-500' },
  { bg: 'bg-rose-50', bgDark: 'bg-rose-900/30', border: 'border-rose-200', borderDark: 'border-rose-700', text: 'text-rose-700', textDark: 'text-rose-400', dot: 'bg-rose-500', ring: 'ring-rose-100', ringDark: 'ring-rose-900/50', gradient: 'from-rose-500 to-pink-500' },
  { bg: 'bg-blue-50', bgDark: 'bg-blue-900/30', border: 'border-blue-200', borderDark: 'border-blue-700', text: 'text-blue-700', textDark: 'text-blue-400', dot: 'bg-blue-500', ring: 'ring-blue-100', ringDark: 'ring-blue-900/50', gradient: 'from-blue-500 to-cyan-500' },
  { bg: 'bg-violet-50', bgDark: 'bg-violet-900/30', border: 'border-violet-200', borderDark: 'border-violet-700', text: 'text-violet-700', textDark: 'text-violet-400', dot: 'bg-violet-500', ring: 'ring-violet-100', ringDark: 'ring-violet-900/50', gradient: 'from-violet-500 to-purple-500' }
];

export default function BuyPlannerCard({ currentUser, holdings, language = 'th', supportLevels, onSaveSupportLevels, darkMode = false, telegramChatId, onSaveTelegramConfig, logos = {}, globalPrices = {}, preSelectSymbol, onPreSelectConsumed }) {
  const labels = useMemo(() => getLabels(language), [language]);
  const [selectedSymbol, setSelectedSymbol] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [support1, setSupport1] = useState('');
  const [support2, setSupport2] = useState('');
  const [support3, setSupport3] = useState('');
  const [support4, setSupport4] = useState('');
  const [support5, setSupport5] = useState('');
  const [support6, setSupport6] = useState('');
  const [support7, setSupport7] = useState('');
  const [justSaved, setJustSaved] = useState(false);
  const [showPaywall, setShowPaywall] = useState(false);
  const { isPro, limits, _freemiumEnabled } = usePlan();
  
  // External symbol price (for symbols not in holdings)
  const [externalPrice, setExternalPrice] = useState(null);
  const [loadingPrice, setLoadingPrice] = useState(false);
  
  // Telegram Modal State
  const [isTelegramModalOpen, setIsTelegramModalOpen] = useState(false);
  const [tempChatId, setTempChatId] = useState('');
  const [linkLoading, setLinkLoading] = useState(false);
  const [linkStatus, setLinkStatus] = useState(''); // '', 'waiting', 'success', 'error'


  const dropdownRef = useRef(null);

  // Auto-select symbol when navigated from context menu "แจ้งเตือนแนวรับ"
  useEffect(() => {
    if (preSelectSymbol) {
      setSelectedSymbol(preSelectSymbol);
      setSearchQuery('');
      setIsOpen(false);
      if (onPreSelectConsumed) onPreSelectConsumed();
    }
  }, [preSelectSymbol, onPreSelectConsumed]);

  // Dark mode theme helpers
  const dm = darkMode;
  const cardBg = dm ? 'bg-[#060d1a] border-indigo-500/20' : 'bg-white border-slate-100';
  const textPrimary = dm ? 'text-slate-100' : 'text-slate-800';
  const textSecondary = dm ? 'text-slate-400' : 'text-slate-500';
  const textMuted = dm ? 'text-slate-500' : 'text-slate-400';
  const inputBg = dm ? 'bg-slate-800/60 border-indigo-500/20 text-slate-100 placeholder-slate-500' : 'border-slate-200 text-slate-800';
  const inputFocus = dm ? 'focus:border-indigo-400 focus:ring-indigo-900/40' : 'focus:border-indigo-500 focus:ring-indigo-100';
  const dropdownBg = dm ? 'bg-[#0a1628] border-indigo-500/20' : 'bg-white border-slate-200';
  const hoverBg = dm ? 'hover:bg-indigo-950/40' : 'hover:bg-slate-50';
  const selectedBg = dm ? 'bg-indigo-900/40 border-l-4 border-indigo-400' : 'bg-indigo-50 border-l-4 border-indigo-500';

  const { symbolMap } = useAllSymbols();
  const activeHoldings = useMemo(() => holdings.filter(h => h.quantity > 0), [holdings]);
  const filteredHoldings = useMemo(() => activeHoldings.filter(h => h.symbol.toLowerCase().includes(searchQuery.toLowerCase())), [activeHoldings, searchQuery]);

  // All-symbol search results (excluding holdings already shown)
  const filteredAllSymbols = useMemo(() => {
    if (!searchQuery || !symbolMap) return [];
    const search = searchQuery.toLowerCase();
    const holdingSet = new Set(activeHoldings.map(h => h.symbol.toUpperCase()));
    const matches = [];
    for (const [sym, desc] of Object.entries(symbolMap)) {
      if (holdingSet.has(sym.toUpperCase())) continue;
      if (sym.toLowerCase().includes(search) || desc.toLowerCase().includes(search)) {
        matches.push({ symbol: sym, name: desc });
      }
    }
    matches.sort((a, b) => {
      const aS = a.symbol.toLowerCase(), bS = b.symbol.toLowerCase();
      if (aS === search) return -1;
      if (bS === search) return 1;
      if (aS.startsWith(search) && !bS.startsWith(search)) return -1;
      if (!aS.startsWith(search) && bS.startsWith(search)) return 1;
      return aS.localeCompare(bS);
    });
    return matches.slice(0, 20);
  }, [symbolMap, searchQuery, activeHoldings]);
  const selectedHolding = useMemo(() => activeHoldings.find(h => h.symbol === selectedSymbol), [activeHoldings, selectedSymbol]);
  
  // For non-held symbols: check globalPrices (Firebase) first, then Twelve Data
  const fetchExternalPrice = useCallback(async (sym) => {
    if (!sym) return;
    // 1. Check globalPrices from Firebase market data (covers XAUUSD, GOLDTHB, crypto, etc.)
    const globalVal = globalPrices[sym];
    if (globalVal && parseFloat(globalVal) > 0) {
      setExternalPrice(parseFloat(globalVal));
      return;
    }
    // 2. Fallback: Twelve Data API
    setLoadingPrice(true);
    try {
      // Map gold symbols to Twelve Data format
      let tdSymbol = sym;
      if (sym === 'XAUUSD') tdSymbol = 'XAU/USD';
      else if (sym === 'GOLDTHB') { setExternalPrice(null); setLoadingPrice(false); return; } // GOLDTHB only from Firebase
      const data = await getQuote(tdSymbol);
      if (data && data.close && !data.code) {
        setExternalPrice(parseFloat(data.close));
      } else {
        setExternalPrice(null);
      }
    } catch {
      setExternalPrice(null);
    }
    setLoadingPrice(false);
  }, [globalPrices]);

  // When selecting a non-held symbol, fetch its price
  useEffect(() => {
    if (selectedSymbol && !selectedHolding) {
      fetchExternalPrice(selectedSymbol);
    } else {
      setExternalPrice(null);
    }
  }, [selectedSymbol, selectedHolding, fetchExternalPrice]);

  const currentPrice = selectedHolding
    ? (selectedSymbol === 'GOLDTHB' ? selectedHolding.currentPriceNative : selectedHolding.currentPriceUSD)
    : (externalPrice || 0);
  const isExternalSymbol = selectedSymbol && !selectedHolding;

  // When selecting a symbol, load existing support levels
  useEffect(() => {
    if (selectedSymbol && supportLevels[selectedSymbol]) {
      const saved = supportLevels[selectedSymbol];
      setSupport1(saved.s1 ? String(saved.s1) : '');
      setSupport2(saved.s2 ? String(saved.s2) : '');
      setSupport3(saved.s3 ? String(saved.s3) : '');
      setSupport4(saved.s4 ? String(saved.s4) : '');
      setSupport5(saved.s5 ? String(saved.s5) : '');
      setSupport6(saved.s6 ? String(saved.s6) : '');
      setSupport7(saved.s7 ? String(saved.s7) : '');
    } else {
      setSupport1('');
      setSupport2('');
      setSupport3('');
      setSupport4('');
      setSupport5('');
      setSupport6('');
      setSupport7('');
    }
  }, [selectedSymbol, supportLevels]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) setIsOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSave = () => {
    const rawValues = [
      parseFloat(support1) || 0,
      parseFloat(support2) || 0,
      parseFloat(support3) || 0,
      parseFloat(support4) || 0,
      parseFloat(support5) || 0,
      parseFloat(support6) || 0,
      parseFloat(support7) || 0,
    ];

    if (!selectedSymbol) return;

    // Freemium Gate: check alert count limit
    if (_freemiumEnabled && !isPro) {
      const existingCount = Object.keys(supportLevels).length;
      const isNew = !supportLevels[selectedSymbol];
      if (isNew && existingCount >= limits.maxAlerts) {
        setShowPaywall(true);
        return;
      }
    }

    // Auto-sort: collect non-zero values, sort descending (highest first = nearest support)
    // S1 = closest to current price, S7 = deepest dip target
    const sortedValues = rawValues.filter(v => v > 0).sort((a, b) => b - a);
    const [s1 = 0, s2 = 0, s3 = 0, s4 = 0, s5 = 0, s6 = 0, s7 = 0] = sortedValues;

    // Reflect sorted values back into the input fields
    setSupport1(s1 > 0 ? String(s1) : '');
    setSupport2(s2 > 0 ? String(s2) : '');
    setSupport3(s3 > 0 ? String(s3) : '');
    setSupport4(s4 > 0 ? String(s4) : '');
    setSupport5(s5 > 0 ? String(s5) : '');
    setSupport6(s6 > 0 ? String(s6) : '');
    setSupport7(s7 > 0 ? String(s7) : '');

    const updated = { ...supportLevels };
    if (sortedValues.length === 0) {
      delete updated[selectedSymbol];
    } else {
      updated[selectedSymbol] = { s1, s2, s3, s4, s5, s6, s7 };
    }
    const wasExisting = !!supportLevels[selectedSymbol];
    onSaveSupportLevels(updated);
    try {
      logEvent(wasExisting ? EVENTS.ALERT_EDITED : EVENTS.ALERT_CREATED, {
        symbol: selectedSymbol,
        source_screen: 'buy_planner',
      });
    } catch {}
    setJustSaved(true);
    setTimeout(() => setJustSaved(false), 2000);
  };

  const handleDelete = () => {
    if (!selectedSymbol) return;
    const updated = { ...supportLevels };
    delete updated[selectedSymbol];
    onSaveSupportLevels(updated);
    try { logEvent(EVENTS.ALERT_DELETED, { symbol: selectedSymbol, source_screen: 'buy_planner' }); } catch {}
    setSupport1('');
    setSupport2('');
    setSupport3('');
    setSupport4('');
    setSupport5('');
    setSupport6('');
    setSupport7('');
  };

  const handleDeleteSymbol = (sym, e) => {
    if (e) e.stopPropagation();
    const updated = { ...supportLevels };
    delete updated[sym];
    onSaveSupportLevels(updated);
    try { logEvent(EVENTS.ALERT_DELETED, { symbol: sym, source_screen: 'buy_planner_chip' }); } catch {}
    if (selectedSymbol === sym) {
      setSupport1('');
      setSupport2('');
      setSupport3('');
      setSupport4('');
      setSupport5('');
      setSupport6('');
      setSupport7('');
      setSelectedSymbol('');
    }
  };

  // Telegram Helpers
  const handleOpenTelegramModal = () => {
    setTempChatId(telegramChatId || '');
    setIsTelegramModalOpen(true);
  };

  // Summary: all saved support level entries (including external/non-held symbols)
  const [externalPrices, setExternalPrices] = useState({});
  
  // Fetch prices for external symbols in summary
  useEffect(() => {
    const externalSyms = Object.keys(supportLevels).filter(sym => !activeHoldings.find(h => h.symbol === sym));
    if (externalSyms.length === 0) return;
    
    const fetchMissing = async () => {
      // Only fetch if it's completely missing (undefined). If it's null, we already tried and failed.
      const missing = externalSyms.filter(s => externalPrices[s] === undefined);
      if (missing.length === 0) return;
      const newPrices = { ...externalPrices };
      // Fill from globalPrices first
      const stillMissing = [];
      for (const sym of missing) {
        const gp = globalPrices[sym];
        if (gp && parseFloat(gp) > 0) {
          newPrices[sym] = parseFloat(gp);
        } else {
          stillMissing.push(sym);
        }
      }
      // Fetch remaining from Twelve Data
      if (stillMissing.length > 0) {
        try {
          const tdSymbols = stillMissing.map(s => s === 'XAUUSD' ? 'XAU/USD' : s).filter(s => s !== 'GOLDTHB');
          if (tdSymbols.length > 0) {
            const data = await getQuote(tdSymbols.join(','));
            if (tdSymbols.length === 1) {
              const origSym = stillMissing[0];
              if (data && data.close && !data.code) {
                newPrices[origSym] = parseFloat(data.close);
              } else {
                newPrices[origSym] = null;
              }
            } else if (data) {
              stillMissing.forEach((sym, i) => {
                const tdKey = tdSymbols[i];
                if (tdKey && data[tdKey] && data[tdKey].close && !data[tdKey].code) {
                  newPrices[sym] = parseFloat(data[tdKey].close);
                } else if (!newPrices[sym]) {
                  newPrices[sym] = null;
                }
              });
            }
          }
          // Mark GOLDTHB as null if not from globalPrices (can't fetch from Twelve Data)
          stillMissing.forEach(s => { if (s === 'GOLDTHB' && newPrices[s] === undefined) newPrices[s] = null; });
        } catch { /* ignore */ }
      }
      setExternalPrices(newPrices);
    };
    fetchMissing();
  }, [supportLevels, activeHoldings]); // eslint-disable-line react-hooks/exhaustive-deps

  const savedEntries = useMemo(() => {
    return Object.entries(supportLevels)
      .map(([symbol, levels]) => {
        const hold = activeHoldings.find(h => h.symbol === symbol);
        const price = hold
          ? (symbol === 'GOLDTHB' ? hold.currentPriceNative : hold.currentPriceUSD)
          : (externalPrices[symbol] || 0);
        return { symbol, ...levels, holding: hold, currentPriceUSD: price, isExternal: !hold };
      })
      .filter(entry => entry.s1 > 0 || entry.s2 > 0 || entry.s3 > 0 || entry.s4 > 0 || entry.s5 > 0 || entry.s6 > 0 || entry.s7 > 0)
      .sort((a, b) => {
        const aMinSupport = Math.max(a.s1 || 0, a.s2 || 0, a.s3 || 0, a.s4 || 0, a.s5 || 0, a.s6 || 0, a.s7 || 0);
        const bMinSupport = Math.max(b.s1 || 0, b.s2 || 0, b.s3 || 0, b.s4 || 0, b.s5 || 0, b.s6 || 0, b.s7 || 0);
        const aRatio = aMinSupport > 0 ? a.currentPriceUSD / aMinSupport : Infinity;
        const bRatio = bMinSupport > 0 ? b.currentPriceUSD / bMinSupport : Infinity;
        return aRatio - bRatio;
      });
  }, [supportLevels, activeHoldings, externalPrices]);

  const getDropPercent = (price) => {
    if (!currentPrice || !price || price <= 0) return null;
    return ((currentPrice - price) / currentPrice) * 100;
  };

  // Get dark-aware support colors
  const getSC = (color, key) => dm ? (color[key + 'Dark'] || color[key]) : color[key];

  return (
    <>
      {showPaywall && (
        <PaywallModal feature="alerts" onClose={() => setShowPaywall(false)} />
      )}
      <div className="space-y-6">
      {/* Main Card */}
      <div className={`rounded-3xl shadow-lg border p-4 sm:p-5 ${cardBg}`}>
        <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between mb-4">
          <div>
            <h3 className={`text-lg font-bold flex items-center gap-2 ${textPrimary}`}>
              <Target className="text-indigo-500" size={20} />
              {labels.title}
            </h3>
          </div>
          
          <button
            onClick={() => {
              if (_freemiumEnabled && !isPro) {
                setShowPaywall(true);
                return;
              }
              handleOpenTelegramModal();
            }}
            className={`flex items-center justify-center gap-2 px-3 py-1.5 text-xs font-bold rounded-xl transition-all shadow-sm shrink-0
              ${telegramChatId 
                ? (dm ? 'bg-emerald-900/30 text-emerald-400 border border-emerald-500/30 hover:bg-emerald-900/50' : 'bg-emerald-50 text-emerald-600 border border-emerald-200 hover:bg-emerald-100')
                : (dm ? 'bg-indigo-900/30 text-indigo-400 border border-indigo-500/30 hover:bg-indigo-900/50' : 'bg-indigo-50 text-indigo-600 border border-indigo-200 hover:bg-indigo-100')}`}
          >
            {telegramChatId ? <CheckCircle size={16} /> : <Bell size={16} />}
            {telegramChatId ? (language === 'th' ? 'เชื่อมต่อ Telegram แล้ว' : 'Telegram Connected') : (language === 'th' ? 'เชื่อมต่อ Telegram' : 'Connect Telegram')}
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          {/* Left: Asset Selector */}
          <div className="lg:col-span-2">
            <h4 className={`text-sm font-bold mb-3 flex items-center gap-2 ${dm ? 'text-slate-300' : 'text-slate-700'}`}>
              <Briefcase size={16} className="text-indigo-500" />
              {labels.selectAsset}
            </h4>

            {/* Dropdown */}
            <div className="relative" ref={dropdownRef}>
              <input
                type="text"
                value={isOpen ? searchQuery : selectedSymbol}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setIsOpen(true);
                  if (e.target.value === '') setSelectedSymbol('');
                }}
                onFocus={() => { setIsOpen(true); setSearchQuery(''); }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && searchQuery.trim()) {
                    e.preventDefault();
                    const sym = searchQuery.trim().toUpperCase();
                    setSelectedSymbol(sym);
                    setSearchQuery('');
                    setIsOpen(false);
                  }
                }}
                placeholder={labels.searchPlaceholder}
                className={`w-full border-2 rounded-xl p-4 text-sm outline-none uppercase font-semibold transition-all focus:ring-4 ${inputBg} ${inputFocus}`}
              />
              {isOpen && (
                <div className={`absolute z-10 w-full mt-2 border rounded-xl shadow-2xl max-h-[60vh] sm:max-h-[28rem] overflow-y-auto ${dropdownBg}`}>
                  {/* Show holdings that match */}
                  {filteredHoldings.length > 0 && (
                    <>
                      <div className={`px-4 py-2 text-[10px] font-bold uppercase tracking-wider ${textMuted}`}>
                        {language === 'th' ? 'ในพอร์ต' : 'In Portfolio'}
                      </div>
                      {filteredHoldings.map(h => (
                        <div
                          key={h.symbol}
                          className={`p-4 cursor-pointer transition-all flex items-center justify-between ${selectedSymbol === h.symbol ? selectedBg : hoverBg}`}
                          onClick={() => { setSelectedSymbol(h.symbol); setSearchQuery(''); setIsOpen(false); }}
                        >
                          <div className="flex items-center gap-3">
                            {logos[h.symbol] ? (
                              <img src={logos[h.symbol]} alt={h.symbol} className="w-8 h-8 rounded-lg object-contain drop-shadow-sm dark:drop-shadow-[0_0_3px_rgba(255,255,255,0.3)] dark:brightness-110" onError={(e) => { e.target.style.display = 'none'; if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex'; }} />
                            ) : null}
                            <div className="w-8 h-8 rounded-lg flex items-center justify-center text-white font-bold text-xs" style={{ backgroundColor: getCategoryColor(h.assetClass), display: logos[h.symbol] ? 'none' : 'flex' }}>
                              {h.symbol.substring(0, 2)}
                            </div>
                            <div>
                              <div className={`font-bold ${textPrimary}`}>{h.symbol}</div>
                              <div className={`text-xs ${textSecondary}`}>{formatMoneyDetail(h.currentPriceUSD)}</div>
                            </div>
                          </div>
                          <div className="text-right">
                            <div className={`text-sm font-semibold ${dm ? 'text-slate-300' : 'text-slate-700'}`}>{formatNumber(h.quantity)}</div>
                            <div className={`text-xs ${textSecondary}`}>{labels.units}</div>
                          </div>
                        </div>
                      ))}
                    </>
                  )}
                  {/* Show matching symbols from full database */}
                  {searchQuery.trim() && filteredAllSymbols.length > 0 && (
                    <>
                      <div className={`px-4 py-2 text-[10px] font-bold uppercase tracking-wider ${textMuted}`}>
                        {language === 'th' ? 'ทั้งหมด' : 'All Symbols'}
                      </div>
                      {filteredAllSymbols.map(item => (
                        <div
                          key={item.symbol}
                          className={`px-4 py-3 cursor-pointer transition-all flex items-center justify-between ${hoverBg}`}
                          onClick={() => { setSelectedSymbol(item.symbol); setSearchQuery(''); setIsOpen(false); }}
                        >
                          <div className="flex items-center gap-3">
                            {logos[item.symbol] ? (
                              <img src={logos[item.symbol]} alt={item.symbol} className="w-8 h-8 rounded-lg object-cover" />
                            ) : (
                              <div className={`w-8 h-8 rounded-lg flex items-center justify-center font-bold text-xs ${dm ? 'bg-slate-700 text-slate-300' : 'bg-slate-100 text-slate-600'}`}>
                                {item.symbol.substring(0, 2)}
                              </div>
                            )}
                            <div>
                              <div className={`font-bold text-sm ${textPrimary}`}>{item.symbol}</div>
                              <div className={`text-xs truncate max-w-[180px] ${textSecondary}`}>{item.name}</div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </>
                  )}
                  {/* Show "add custom symbol" option only if it doesn't match an existing holding or known symbol */}
                  {searchQuery.trim() && !activeHoldings.some(h => h.symbol.toUpperCase() === searchQuery.trim().toUpperCase()) && filteredAllSymbols.length === 0 && (
                    <div
                      className={`p-4 cursor-pointer transition-all flex items-center gap-3 border-t ${dm ? 'border-indigo-500/10' : 'border-slate-100'} ${hoverBg}`}
                      onClick={() => {
                        const sym = searchQuery.trim().toUpperCase();
                        setSelectedSymbol(sym);
                        setSearchQuery('');
                        setIsOpen(false);
                      }}
                    >
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${dm ? 'bg-indigo-900/60 text-indigo-400' : 'bg-indigo-100 text-indigo-600'}`}>
                        <Plus size={16} />
                      </div>
                      <div>
                        <div className={`font-bold ${textPrimary}`}>{searchQuery.trim().toUpperCase()}</div>
                        <div className={`text-xs ${dm ? 'text-indigo-400' : 'text-indigo-500'}`}>
                          {language === 'th' ? 'เพิ่มหุ้นนอกพอร์ต' : 'Add external symbol'}
                        </div>
                      </div>
                      <div className={`ml-auto text-xs font-bold px-2 py-1 rounded-lg ${dm ? 'bg-indigo-900/40 text-indigo-400' : 'bg-indigo-50 text-indigo-600'}`}>
                        Enter ↵
                      </div>
                    </div>
                  )}
                  {filteredHoldings.length === 0 && !searchQuery.trim() && (
                    <div className={`p-6 text-center ${textMuted}`}>
                      <Search size={32} className="mx-auto mb-2 opacity-50" />
                      <p>{labels.noAssetFound}</p>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Current Holding / External Symbol Info */}
            {selectedSymbol && (selectedHolding || isExternalSymbol) && (
              <div className={`mt-3 rounded-xl p-3 border ${dm ? 'bg-slate-800/40 border-indigo-500/15' : 'bg-gradient-to-br from-slate-50 to-slate-100 border-slate-200'}`}>
                <div className="flex items-center gap-3 mb-2">
                  {logos[selectedSymbol] ? (
                    <img src={logos[selectedSymbol]} alt={selectedSymbol} className="w-8 h-8 rounded-lg object-contain drop-shadow-sm dark:drop-shadow-[0_0_3px_rgba(255,255,255,0.3)] dark:brightness-110" onError={(e) => { e.target.style.display = 'none'; if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex'; }} />
                  ) : null}
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-white font-bold text-xs shadow-md`} style={{ backgroundColor: isExternalSymbol ? '#6366f1' : getCategoryColor(selectedHolding?.assetClass), display: logos[selectedSymbol] ? 'none' : 'flex' }}>
                    {selectedSymbol.substring(0, 2)}
                  </div>
                  <div className="flex-1">
                    <div className={`font-bold text-lg ${textPrimary}`}>{selectedSymbol}</div>
                    {isExternalSymbol && (
                      <div className={`text-[10px] font-bold px-2 py-0.5 rounded-full inline-block ${dm ? 'bg-indigo-900/50 text-indigo-400' : 'bg-indigo-100 text-indigo-600'}`}>
                        {language === 'th' ? '📡 นอกพอร์ต — Watchlist' : '📡 External — Watchlist'}
                      </div>
                    )}
                  </div>
                </div>
                <div className={`grid ${isExternalSymbol ? 'grid-cols-1' : 'grid-cols-2'} gap-2`}>
                  {selectedHolding && (
                    <>
                      <div className={`rounded-lg p-2.5 ${dm ? 'bg-slate-900/50' : 'bg-white'}`}>
                        <p className={`text-[10px] mb-0.5 ${textMuted}`}>{labels.holdingQty}</p>
                        <p className={`text-sm font-bold font-mono ${textPrimary}`}>{formatNumber(selectedHolding.quantity)}</p>
                      </div>
                      <div className={`rounded-lg p-2.5 ${dm ? 'bg-slate-900/50' : 'bg-white'}`}>
                        <p className={`text-[10px] mb-0.5 ${textMuted}`}>{labels.avgCost}</p>
                        <p className={`text-sm font-bold font-mono ${textPrimary}`}>{fmtPrice(selectedSymbol, selectedSymbol === 'GOLDTHB' ? selectedHolding.avgCostNative : selectedHolding.avgCostUSD)}</p>
                      </div>
                    </>
                  )}
                  <div className={`rounded-lg p-2.5 ${dm ? 'bg-slate-800/70' : 'bg-white'}`}>
                    <p className={`text-[10px] mb-0.5 ${textMuted}`}>{labels.currentPrice}</p>
                    {loadingPrice ? (
                      <p className={`text-sm font-bold font-mono ${textSecondary} animate-pulse`}>Loading...</p>
                    ) : currentPrice > 0 ? (
                      <p className={`text-sm font-bold font-mono ${dm ? 'text-blue-400' : 'text-blue-600'}`}>{fmtPrice(selectedSymbol, currentPrice)}</p>
                    ) : (
                      <p className={`text-sm font-bold font-mono ${dm ? 'text-rose-400' : 'text-rose-500'}`}>{language === 'th' ? 'ไม่พบราคา' : 'N/A'}</p>
                    )}
                  </div>
                  {selectedHolding && (
                    <div className={`rounded-lg p-2.5 ${dm ? 'bg-slate-800/70' : 'bg-white'}`}>
                      <p className={`text-[10px] mb-0.5 ${textMuted}`}>{labels.totalValue}</p>
                      <p className={`text-sm font-bold font-mono ${textPrimary}`}>{fmtValue(selectedSymbol, selectedSymbol === 'GOLDTHB' ? selectedHolding.currentValueNative : selectedHolding.currentValueUSD)}</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Right: Support Level Inputs */}
          <div className="lg:col-span-3">
            {(selectedHolding || isExternalSymbol) ? (
              <div className="space-y-3">
                {/* Support Level Inputs — compact 2-column grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                {[
                  { label: 'S1', value: support1, setter: setSupport1, color: SUPPORT_COLORS[0] },
                  { label: 'S2', value: support2, setter: setSupport2, color: SUPPORT_COLORS[1] },
                  { label: 'S3', value: support3, setter: setSupport3, color: SUPPORT_COLORS[2] },
                  { label: 'S4', value: support4, setter: setSupport4, color: SUPPORT_COLORS[3] },
                  { label: 'S5', value: support5, setter: setSupport5, color: SUPPORT_COLORS[4] },
                  { label: 'S6', value: support6, setter: setSupport6, color: SUPPORT_COLORS[5] },
                  { label: 'S7', value: support7, setter: setSupport7, color: SUPPORT_COLORS[6] }
                ].map((level, i) => {
                  const dropPct = getDropPercent(parseFloat(level.value));
                  const status = getSupportStatus(currentPrice, parseFloat(level.value));
                  const isHit = status?.type === 'below' || status?.type === 'near';

                  return (
                    <div key={i} className={`rounded-lg border px-2 py-1.5 transition-all duration-200 ${isHit
                      ? `${getSC(level.color, 'bg')} ${getSC(level.color, 'border')} shadow-sm`
                      : dm ? 'border-indigo-500/15 bg-slate-800/40 hover:border-indigo-500/30' : 'border-slate-200 bg-white hover:border-slate-300'
                      }`}>
                      <div className="flex items-center gap-2">
                        <div className={`w-1.5 h-1.5 rounded-full ${level.color.dot} shrink-0`}></div>
                        <span className={`text-[11px] font-bold w-6 shrink-0 ${isHit ? getSC(level.color, 'text') : textPrimary}`}>{level.label}</span>
                        <div className="relative flex-1">
                          <span className={`absolute left-2 top-1/2 -translate-y-1/2 text-[12px] font-mono ${textMuted}`}>{selectedSymbol === 'GOLDTHB' ? '฿' : '$'}</span>
                          <input
                            type="number"
                            min="0"
                            step="any"
                            value={level.value}
                            onChange={(e) => level.setter(e.target.value)}
                            placeholder={labels.pricePlaceholder}
                            className={`w-full border rounded-md pl-5 pr-1.5 py-1 text-[12px] font-mono font-semibold outline-none transition-all focus:ring-2 ${isHit
                              ? `${getSC(level.color, 'border')} ${getSC(level.color, 'ring')} ${dm ? 'bg-slate-900/50 text-slate-100' : 'bg-white/80'}`
                              : dm ? 'border-indigo-500/20 bg-slate-900/50 text-slate-100 focus:border-indigo-400 focus:ring-indigo-900/40 placeholder-slate-500' : 'border-slate-200 focus:border-indigo-500 focus:ring-indigo-100'
                              }`}
                          />
                        </div>
                        {dropPct !== null && dropPct > 0 && !isHit && (
                          <span className={`text-[10px] font-mono whitespace-nowrap ${textSecondary}`}>
                            -{dropPct.toFixed(1)}%
                          </span>
                        )}
                        {status && isHit && (
                          <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold whitespace-nowrap ${status.type === 'below'
                            ? `bg-gradient-to-r ${level.color.gradient} text-white`
                            : `${getSC(level.color, 'bg')} ${getSC(level.color, 'text')}`
                            }`}>
                            {status.type === 'below' ? '🔥' : '⚠️'}
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
                </div>

                {/* Action Buttons */}
                <div className="flex flex-wrap gap-3 pt-2">
                  <button
                    type="button"
                    onClick={handleSave}
                    className={`inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-semibold shadow-sm transition-all ${justSaved
                      ? 'bg-emerald-600 text-white'
                      : 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white hover:from-indigo-700 hover:to-purple-700'
                      }`}
                  >
                    {justSaved ? <CheckCircle size={16} /> : <Save size={16} />}
                    {justSaved ? labels.saved : labels.save}
                  </button>
                  {supportLevels[selectedSymbol] && (
                    <button
                      type="button"
                      onClick={handleDelete}
                      className={`inline-flex items-center gap-2 rounded-xl border px-4 py-2 text-sm font-semibold transition-all ${dm
                        ? 'border-rose-800 bg-rose-900/30 text-rose-400 hover:bg-rose-900/50'
                        : 'border-rose-200 bg-white text-rose-600 hover:bg-rose-50'
                        }`}
                    >
                      <Trash2 size={16} />
                      {labels.delete}
                    </button>
                  )}
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </div>

      {/* Summary Table - All Saved Support Levels */}
      <div className={`rounded-3xl shadow-lg border p-3 sm:p-4 ${cardBg}`}>
        <div className="mb-3">
          <h3 className={`text-sm font-bold flex items-center gap-2 ${textPrimary}`}>
            <AlertTriangle className="text-amber-500" size={16} />
            {labels.summaryTitle}
          </h3>
        </div>

        {savedEntries.length > 0 ? (
          <div className="space-y-2">
            {savedEntries.map((entry) => {
              const price = entry.currentPriceUSD;
              const statuses = [
                { level: 1, price: entry.s1, status: getSupportStatus(price, entry.s1), color: SUPPORT_COLORS[0] },
                { level: 2, price: entry.s2, status: getSupportStatus(price, entry.s2), color: SUPPORT_COLORS[1] },
                { level: 3, price: entry.s3, status: getSupportStatus(price, entry.s3), color: SUPPORT_COLORS[2] },
                { level: 4, price: entry.s4, status: getSupportStatus(price, entry.s4), color: SUPPORT_COLORS[3] },
                { level: 5, price: entry.s5, status: getSupportStatus(price, entry.s5), color: SUPPORT_COLORS[4] },
                { level: 6, price: entry.s6, status: getSupportStatus(price, entry.s6), color: SUPPORT_COLORS[5] },
                { level: 7, price: entry.s7, status: getSupportStatus(price, entry.s7), color: SUPPORT_COLORS[6] }
              ].filter(s => s.price > 0);

              const hasAlert = statuses.some(s => s.status?.type === 'below' || s.status?.type === 'near');

              return (
                <div
                  key={entry.symbol}
                  className={`group relative rounded-2xl md:rounded-lg px-3 py-2.5 transition-all duration-300 cursor-pointer ${
                    dm ? 'md:hover:brightness-110' : 'md:hover:bg-slate-50'
                  } ${hasAlert ? (dm ? 'ring-1 ring-amber-400/50' : 'ring-1 ring-amber-300') : ''}`}
                  style={{
                    background: dm
                      ? 'linear-gradient(135deg, #0b1426 0%, #080f1d 100%)'
                      : '#ffffff',
                    boxShadow: dm
                      ? 'inset 0 1px 0 0 rgba(255,255,255,0.14), inset 0 0 0 1px rgba(255,255,255,0.04), inset 0 -10px 20px -14px rgba(0,0,0,0.6), 0 4px 12px -6px rgba(0,0,0,0.5)'
                      : 'inset 0 1px 0 0 rgba(255,255,255,0.8), inset 0 0 0 1px rgba(15,23,42,0.05), 0 4px 12px -6px rgba(15,23,42,0.12)',
                  }}
                  onClick={() => { setSelectedSymbol(entry.symbol); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                >
                  <div className="flex items-center gap-2.5 pr-8">
                    {/* Symbol */}
                    <div className="flex items-center gap-2 shrink-0">
                      <div className={`w-8 h-8 flex items-center justify-center shrink-0 overflow-hidden rounded-lg ring-1 ${dm ? 'ring-white/10' : 'ring-slate-200'} shadow-sm`}>
                        {logos[entry.symbol] ? (
                          <img src={logos[entry.symbol]} alt={entry.symbol} className="w-8 h-8 rounded-lg object-contain drop-shadow-sm dark:drop-shadow-[0_0_3px_rgba(255,255,255,0.3)] dark:brightness-110" onError={(e) => { e.target.style.display = 'none'; if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex'; }} />
                        ) : null}
                        <div className="w-8 h-8 rounded-lg flex items-center justify-center text-white font-bold text-[10px]" style={{ backgroundColor: entry.isExternal ? '#6366f1' : getCategoryColor(entry.holding?.assetClass), display: logos[entry.symbol] ? 'none' : 'flex' }}>
                          {entry.symbol.substring(0, 2)}
                        </div>
                      </div>
                      <div>
                        <div className={`text-xs font-bold flex items-center gap-1 ${textPrimary}`}>
                          {entry.symbol}
                          {entry.isExternal && (
                            <span className={`text-[8px] font-bold px-1 py-0 rounded-full ${dm ? 'bg-indigo-900/50 text-indigo-400' : 'bg-indigo-100 text-indigo-500'}`}>📡</span>
                          )}
                          {hasAlert && <AlertTriangle className="text-amber-500 animate-pulse" size={12} />}
                        </div>
                        <div className={`text-[10px] font-mono ${textSecondary}`}>{price > 0 ? fmtPrice(entry.symbol, price) : '...'}</div>
                      </div>
                    </div>

                    {/* Support Levels */}
                    <div className="flex-1 flex flex-wrap gap-1.5">
                      {statuses.map((s) => {
                        const isHit = s.status?.type === 'below' || s.status?.type === 'near';
                        return (
                          <div
                            key={s.level}
                            className={`flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-semibold border transition-all ${isHit
                              ? `${getSC(s.color, 'bg')} ${getSC(s.color, 'border')} ${getSC(s.color, 'text')} shadow-sm`
                              : dm ? 'bg-slate-900/50 border-indigo-500/15 text-slate-300' : 'bg-white border-slate-200 text-slate-600'
                              }`}
                          >
                            <div className={`w-1.5 h-1.5 rounded-full ${s.color.dot}`}></div>
                            <span>S{s.level}: {fmtPrice(entry.symbol, s.price)}</span>
                            {isHit && (
                              <span className="text-[9px]">{s.status.type === 'below' ? '🔥' : '⚠️'}</span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  <button 
                    type="button"
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleDeleteSymbol(entry.symbol, e); }}
                    className={`absolute top-1.5 right-1.5 z-10 p-1.5 rounded-md opacity-50 hover:opacity-100 transition-opacity ${dm ? 'hover:bg-rose-900/50 text-rose-400 cursor-pointer' : 'hover:bg-rose-100 text-rose-500 cursor-pointer'}`}
                    title={language === 'th' ? 'ลบออก' : 'Remove'}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              );
            })}
          </div>
        ) : (
          <div className={`p-6 text-center rounded-xl border-2 border-dashed ${dm ? 'bg-slate-900/30 border-indigo-500/15' : 'bg-slate-50 border-slate-200'}`}>
            <Target size={48} className={`mx-auto mb-3 ${dm ? 'text-slate-600' : 'text-slate-300'}`} />
            <p className={`font-medium ${textSecondary}`}>{labels.noTargets}</p>
            <p className={`text-xs mt-1 ${textMuted}`}>{labels.noTargetsSubtitle}</p>
          </div>
        )}
      </div>

      {/* Telegram Connect Modal */}
      {isTelegramModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className={`relative w-full max-w-md p-6 rounded-2xl shadow-2xl ${dm ? 'bg-slate-800 border border-slate-700' : 'bg-white border border-slate-200'}`}>
            <button 
              onClick={() => setIsTelegramModalOpen(false)}
              className={`absolute top-4 right-4 p-2 rounded-full transition-colors ${dm ? 'text-slate-400 hover:bg-slate-700 hover:text-white' : 'text-slate-500 hover:bg-slate-100'}`}
            >
              <X size={20} />
            </button>
            
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-blue-100 text-blue-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <Bell size={32} />
              </div>
              <h2 className={`text-xl font-bold ${textPrimary}`}>เชื่อมต่อการแจ้งเตือน</h2>
            </div>

            {telegramChatId ? (
              <div className="text-center space-y-4">
                <div className={`p-4 rounded-xl text-sm mb-2 ${dm ? 'bg-emerald-900/20 text-emerald-400 border border-emerald-800/50' : 'bg-emerald-50 text-emerald-600 border border-emerald-100'}`}>
                  <p className="font-bold text-lg"><CheckCircle className="inline-block mr-2 mb-0.5" size={20}/>เชื่อมต่อบัญชีเรียบร้อยแล้ว</p>
                  <p className="mt-2 opacity-90">ระบบจะทำการแจ้งเตือนผ่าน Telegram ทันทีที่ราคาตกถึงแนวรับที่คุณตั้งเป้าไว้ (ตลอด 24 ชม.)</p>
                </div>
                
                <div className="flex justify-center gap-3">
                  <button
                    onClick={() => {
                      if (onSaveTelegramConfig) onSaveTelegramConfig('');
                      setIsTelegramModalOpen(false);
                    }}
                    className={`px-5 py-3 rounded-xl font-bold text-sm transition-colors border ${dm ? 'border-rose-900/50 text-rose-400 hover:bg-rose-900/30' : 'border-rose-200 text-rose-500 hover:bg-rose-50'}`}
                  >
                    ยกเลิกการเชื่อมต่อ
                  </button>
                  <button
                    onClick={() => setIsTelegramModalOpen(false)}
                    className="px-6 py-3 flex-1 rounded-xl font-bold text-sm bg-indigo-600 text-white hover:bg-indigo-700 transition-colors"
                  >
                    ปิดหน้าต่าง
                  </button>
                </div>
              </div>
            ) : (
              <div className="text-center space-y-5">
                <div className={`p-5 rounded-xl text-sm ${dm ? 'bg-blue-900/20 text-blue-300 border border-blue-800/50' : 'bg-blue-50 text-blue-800 border border-blue-100'}`}>
                  <p className="font-bold mb-2 text-base">วิธีเชื่อมต่อแสนง่าย:</p>
                  <p className="opacity-90 leading-relaxed mb-5">
                    กดปุ่มด้านล่างเพื่อเปิดแอป Telegram จากนั้นกดคำว่า <b>START</b> ด้านล่างแชทบอท เพื่อปลดล็อคการแจ้งเตือนอัตโนมัติ 24 ชม.
                  </p>
                  
                  {linkStatus === 'waiting' && (
                    <div className={`mb-4 p-3 rounded-lg text-xs ${dm ? 'bg-amber-900/30 text-amber-300 border border-amber-700/50' : 'bg-amber-50 text-amber-700 border border-amber-200'}`}>
                      <p className="font-bold">⏳ รอการยืนยันจาก Telegram...</p>
                      <p className="mt-1 opacity-80">กรุณากด START ในแอป Telegram ที่เปิดขึ้น ระบบจะตรวจจับการเชื่อมต่อให้อัตโนมัติ (ลิงก์หมดอายุใน 10 นาที)</p>
                    </div>
                  )}

                  {linkStatus === 'error' && (
                    <div className={`mb-4 p-3 rounded-lg text-xs ${dm ? 'bg-rose-900/30 text-rose-300 border border-rose-700/50' : 'bg-rose-50 text-rose-700 border border-rose-200'}`}>
                      <p className="font-bold">❌ สร้างลิงก์ไม่สำเร็จ</p>
                      <p className="mt-1 opacity-80">กรุณาตรวจสอบอินเทอร์เน็ตและลองใหม่อีกครั้ง</p>
                    </div>
                  )}

                  <button
                    disabled={linkLoading}
                    onClick={async () => {
                      try {
                        setLinkLoading(true);
                        setLinkStatus('');
                        const auth = getAuth();
                        if (!auth.currentUser) {
                          alert('กรุณาเข้าสู่ระบบก่อนเชื่อมต่อ Telegram');
                          setLinkLoading(false);
                          return;
                        }
                        const token = await auth.currentUser.getIdToken();
                        const res = await fetch(TELEGRAM_LINK_URL, {
                          headers: { Authorization: 'Bearer ' + token },
                        });
                        if (!res.ok) {
                          const errBody = await res.text().catch(() => '');
                          console.error('createTelegramLink failed:', res.status, errBody);
                          throw new Error(`สร้างลิงก์ไม่สำเร็จ (${res.status})`);
                        }
                        const { url } = await res.json();
                        window.open(url, '_blank', 'noopener');
                        setLinkStatus('waiting');
                        setLinkLoading(false);
                      } catch (err) {
                        console.error('createTelegramLink error', err);
                        setLinkStatus('error');
                        setLinkLoading(false);
                      }
                    }}
                    className={`inline-flex items-center gap-2 bg-[#0088cc] hover:bg-[#0077b5] text-white px-6 py-3.5 rounded-xl font-bold text-[15px] transition-all hover:scale-105 shadow-md shadow-blue-500/20 w-full justify-center ${linkLoading ? 'opacity-60 cursor-not-allowed' : ''}`}
                  >
                    {linkLoading ? (
                      <>
                        <svg className="w-5 h-5 animate-spin" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg>
                        กำลังสร้างลิงก์...
                      </>
                    ) : (
                      <>
                        <svg className="w-5 h-5 fill-current" viewBox="0 0 24 24"><path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.894 8.221l-1.97 9.28c-.145.658-.537.818-1.084.508l-3-2.21-1.446 1.394c-.14.18-.357.295-.6.295-.002 0-.003 0-.005 0l.213-3.054 5.56-5.022c.24-.213-.054-.334-.373-.121l-6.869 4.326-2.96-.924c-.64-.203-.658-.64.135-.954l11.566-4.458c.538-.196 1.006.128.832.94z"/></svg>
                        {linkStatus === 'waiting' ? 'สร้างลิงก์ใหม่' : 'เชื่อมต่อ Telegram ทันที'}
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
    </>
  );
}

// Export helper for Dashboard alerts
export { getSupportStatus, SUPPORT_COLORS };
